// Git Discard Tool - Discards changes to files (reverts to last committed state)
// Extends WorkspaceTool - has access to appType, fs, and git

import { WorkspaceTool } from "../../base/WorkspaceTool.js";
import { logger } from "../../../../../../core/logger.js";

export class GitDiscardTool extends WorkspaceTool {
  constructor() {
    super();
    this.toolName = 'gitDiscardTool';
  }
  /**
   * Discard changes to files (revert to last committed state)
   * @param {string|string[]} [filepaths=[]] - File path(s) to discard (use '*' or [] for all)
   * @param {Object} [workspaceManager] - Optional workspace manager instance
   * @returns {Promise<Object>} Result with discard status
   */
  async gitDiscard(filepaths = [], workspaceManager = null) {
    try {
      // Route based on app type
      if (this.appType === "browser") {
        return await this.gitDiscardForBrowser(filepaths, workspaceManager);
      }

      if (this.appType === "local") {
        return await this.gitDiscardForLocal(filepaths, workspaceManager);
      }

      return this.returnError(
        {},
        `App type '${this.appType}' not yet implemented for gitDiscard`
      );
    } catch (error) {
      logger.error(
        {
          tags: "tools|gitdiscard|error",
          color1: "red",
        },
        "Failed to discard file changes",
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Execute git discard for browser apps using isomorphic-git
   */
  async gitDiscardForBrowser(filepaths, workspaceManager) {
    const fs = this.fs?.promises;
    if (!fs) {
      return this.returnError(
        {},
        "Lightning FS not available - browser app filesystem not initialized"
      );
    }

    if (!this.git) {
      return this.returnError({}, "Isomorphic-git not available");
    }

    const dir = "/files";

    // Check if .git exists
    const gitExists = await this.checkGitExists(fs);
    if (!gitExists) {
      return this.returnError({}, "Git repository not initialized");
    }

    // Normalize filepaths to array
    let filesToDiscard = [];
    if (
      filepaths === "*" ||
      (Array.isArray(filepaths) && filepaths.length === 0)
    ) {
      // Discard all changes - get all unstaged files from status
      const statusResult = await this.git.statusMatrix({
        fs: this.fs,
        dir,
        filter: (f) => {
          return (
            !f.startsWith(".git/") &&
            f !== ".git" &&
            !f.startsWith(".revolt/") &&
            f !== ".revolt"
          );
        },
      });

      // Get files that have unstaged changes
      // A file has unstaged changes if the working directory differs from the index
      // This includes files with both staged and unstaged changes
      for (const [
        filepath,
        headStatus,
        workdirStatus,
        stageStatus,
      ] of statusResult) {
        if (
          filepath.startsWith(".git/") ||
          filepath === ".git" ||
          filepath.startsWith(".revolt/") ||
          filepath === ".revolt"
        ) {
          continue;
        }
        // A file has unstaged changes if the working directory differs from the index
        // This includes:
        // - Modified files (workdir !== stage)
        // - Deleted files (workdir === 0, stage !== 0)
        // - Untracked files (head === 0, stage === 0, workdir !== 0)
        const isTracked = headStatus !== 0 || stageStatus !== 0;
        const hasUnstagedChanges =
          (isTracked && workdirStatus !== stageStatus) ||
          (!isTracked && workdirStatus !== 0);

        if (hasUnstagedChanges) {
          filesToDiscard.push(filepath);
        }
      }
    } else if (typeof filepaths === "string") {
      filesToDiscard = [filepaths];
    } else if (Array.isArray(filepaths)) {
      filesToDiscard = filepaths;
    } else {
      return this.returnError({}, "Invalid filepaths parameter");
    }

    if (filesToDiscard.length === 0) {
      return this.returnSuccess(
        {
          discardedCount: 0,
          discardedFiles: [],
        },
        "No files to discard"
      );
    }

    // Discard each file by reverting working directory to match the index
    // This discards UNSTAGED changes only (not staged changes)
    const discardedFiles = [];
    const errors = [];

    logger.log(
      {
        tags: "tools|gitdiscard|info",
        color1: "lime",
      },
      `Starting discard operation for ${filesToDiscard.length} files`,
      {
        filesToDiscard: filesToDiscard.slice(0, 10), // Log first 10
      }
    );

    for (const filepath of filesToDiscard) {
      try {
        // Get status to determine if file is in index
        const statusMatrix = await this.git.statusMatrix({
          fs: this.fs,
          dir,
          filepaths: [filepath],
        });

        if (statusMatrix.length === 0) {
          // File not tracked - delete it (untracked file)
          try {
            await fs.unlink(`${dir}/${filepath}`);
            discardedFiles.push(filepath);
          } catch (unlinkError) {
            if (unlinkError.code !== "ENOENT") {
              throw unlinkError;
            }
            // File already doesn't exist, that's fine
            discardedFiles.push(filepath);
          }
          continue;
        }

        const [, headStatus, workdirStatus, stageStatus] = statusMatrix[0];

        // If file exists in index (staged), checkout from index to discard unstaged changes
        if (stageStatus !== 0) {
          // File is in index - checkout from index (no ref = checkout from index)
          // force: true is needed to overwrite working directory changes
          await this.git.checkout({
            fs: this.fs,
            dir,
            filepaths: [filepath],
            force: true,
          });
          discardedFiles.push(filepath);
        } else if (headStatus !== 0 && workdirStatus !== 0) {
          // File is tracked (in HEAD) but not in index and has working dir changes
          // This means it was unstaged - checkout from HEAD to discard changes
          // force: true is needed to overwrite working directory changes
          await this.git.checkout({
            fs: this.fs,
            dir,
            ref: "HEAD",
            filepaths: [filepath],
            force: true,
          });
          discardedFiles.push(filepath);
        } else if (workdirStatus !== 0) {
          // File has unstaged changes but is not in index and not in HEAD
          // This means it's an untracked file - delete it
          try {
            await fs.unlink(`${dir}/${filepath}`);
            discardedFiles.push(filepath);
          } catch (unlinkError) {
            if (unlinkError.code !== "ENOENT") {
              throw unlinkError;
            }
            discardedFiles.push(filepath);
          }
        }
      } catch (error) {
        errors.push({ path: filepath, error: error.message });
        logger.warn(
          {
            tags: "tools|gitdiscard|checkout",
            color1: "yellow",
          },
          `Failed to discard file: ${filepath}`,
          error
        );
      }
    }

    logger.log(
      {
        tags: "tools|gitdiscard|info",
        color1: "lime",
      },
      `Discarded changes to ${discardedFiles.length} files`,
      {
        discardedCount: discardedFiles.length,
        errors: errors.length,
      }
    );

    // After discarding, we used to check for remaining changes here.
    // The result was only used for marking workspace "clean" in the obsolete pseudo-unsaved system,
    // so the check is no longer needed.

    // Sync changes with workspace manager to update UI
    if (workspaceManager?.syncOpenFiles) {
      await workspaceManager.syncOpenFiles(discardedFiles);
    }

    return this.returnSuccess(
      {
        discardedCount: discardedFiles.length,
        discardedFiles,
        errors: errors.length > 0 ? errors : undefined,
      },
      `Discarded changes to ${discardedFiles.length} file(s) successfully`
    );
  }

  /**
   * Discard unstaged / untracked changes via Revolt Desktop IPC (`git checkout`
   * + unlink for untracked), mirroring `gitDiscardForBrowser` semantics.
   */
  async gitDiscardForLocal(filepaths, workspaceManager) {
    const api = this.getWorkspaceLocalAppApi();
    if (!api?.gitDiscard) {
      return this.returnError(
        {},
        "Local git discard requires Revolt Desktop (workspaceLocalAppAPI.gitDiscard)."
      );
    }

    const root = this.getLocalWorkingDirectoryRoot();
    if (!root) {
      return this.returnError(
        {},
        "Local app has no working directory (settings.workingDirectoryPath)."
      );
    }

    const discardAll =
      filepaths === "*" ||
      (Array.isArray(filepaths) && filepaths.length === 0);

    const pathsPayload = discardAll
      ? "*"
      : typeof filepaths === "string"
        ? filepaths
        : filepaths;

    const res = await api.gitDiscard({
      rootPath: root,
      paths: pathsPayload,
    });

    if (!res?.success) {
      if (res?.gitNotInstalled) {
        return this.returnError(
          { gitNotInstalled: true },
          res.error ||
            "Git is not installed. Install Git and make sure it's on your PATH to use source control in local workspaces."
        );
      }
      return this.returnError({}, res?.error || "Failed to discard changes");
    }

    const raw = res.discardedFiles || [];
    const displayPaths = raw.map((p) =>
      typeof p === "string" && p.startsWith("/") ? p : `/${p}`
    );

    logger.log(
      { tags: "tools|gitdiscard|local|info", color1: "lime" },
      `Discarded ${displayPaths.length} paths (local)`
    );

    if (workspaceManager?.syncOpenFiles) {
      await workspaceManager.syncOpenFiles(
        displayPaths,
        "git_discard_local"
      );
    }

    return this.returnSuccess(
      {
        discardedCount:
          typeof res.discardedCount === "number"
            ? res.discardedCount
            : displayPaths.length,
        discardedFiles: displayPaths,
        errors: res.errors,
      },
      displayPaths.length > 0
        ? `Discarded changes to ${displayPaths.length} file(s) successfully`
        : "No files to discard"
    );
  }

  /**
   * Check if .git directory exists
   */
  async checkGitExists(fs) {
    try {
      const stats = await fs.stat("/files/.git");
      const isDir =
        typeof stats.isDirectory === "function"
          ? stats.isDirectory()
          : stats.type === "dir";
      return isDir;
    } catch (error) {
      return false;
    }
  }
}
